<template>
    <section ref="profileSearch" class="flex flex-col gap-2" aria-labelledby="profile-search-heading" data-testid="link-profile-search">
        <div class="flex items-center justify-between gap-4">
            <h3 id="profile-search-heading" class="scaffold-body-small">Link collaboration profile</h3>
            <button type="button" class="body-small text-valence-grey-750 hover:underline" :disabled="isLinking" @click="dismissSearch">Not now</button>
        </div>

        <div class="flex flex-col gap-3 rounded-lgr border border-valence-grey-250 bg-white p-4">
            <div class="flex flex-col items-stretch gap-3 md:gap-6 sm:flex-row sm:items-center">
                <BaseSearchInput
                    v-model="searchQuery"
                    aria-label="Search collaboration profiles"
                    placeholder="Search collaboration profiles"
                    :disabled="isLinking"
                    class="body-small" />
                <PublicProfileRequestLinkAction
                    v-if="profiles.length > 0"
                    :copied="isCopied"
                    hide-dismiss
                    :disabled="isLinking || isCopying"
                    size="sm"
                    class="min-w-40"
                    @copy="copyRequestLink"
                    @dismiss="dismissSearch" />
            </div>

            <BaseNotification v-if="searchError" theme="error" text="Searching for profiles failed." hide-dismiss />
            <BaseNotification v-if="showLinkProfileError" theme="error" text="Linking collaboration profile failed." @dismiss="linkProfileError = null" />
            <BaseNotification v-if="requestCopyFailed" theme="error" text="Copying the request link failed." @dismiss="requestCopyFailed = false" />

            <p class="sr-only" role="status">{{ resultStatus }}</p>

            <div v-if="isSearching" class="flex items-center justify-center gap-2 py-8 text-valence-grey-750">
                <LoadingSpinner class="size-4" aria-hidden="true" />
                <span class="body-small">Searching...</span>
            </div>

            <template v-else-if="filteredProfiles.length > 0">
                <p class="scaffold-details-small text-valence-grey-600">{{ searchHeading }}</p>
                <ul
                    ref="resultsList"
                    class="divide-y divide-valence-grey-200 border-t border-valence-grey-200 max-h-80 overflow-auto"
                    aria-label="Collaboration profile search results">
                    <li v-for="profile in filteredProfiles" :key="profile.id" class="flex items-center gap-3 py-3 pr-3">
                        <BaseAvatar size="sm" theme="primary" :initials="getFirstLastInitials(profile.name ?? '')" />
                        <span class="flex min-w-0 grow flex-col">
                            <span class="body-small-semibold truncate">{{ profile.name }}</span>
                            <span v-if="subtitleFor(profile)" class="body-small text-valence-grey-750 truncate">{{ subtitleFor(profile) }}</span>
                        </span>
                        <BaseButton
                            theme="primary"
                            size="sm"
                            class="flex gap-2 items-center min-w-18 justify-center"
                            :disabled="isLinking"
                            :aria-disabled="isLinking"
                            :aria-label="`Link ${profile.name ?? 'collaboration profile'}`"
                            @click="linkSelectedProfile(profile)">
                            <span>Link</span>
                            <LoadingSpinner v-if="isLinking && linkingProfileUserId === profile.user" class="size-3.5" aria-hidden="true" />
                        </BaseButton>
                    </li>
                    <li v-if="isLoadingMore" class="flex items-center justify-center gap-2 py-3 text-valence-grey-750">
                        <LoadingSpinner class="size-4" aria-hidden="true" />
                        <span class="body-small">Loading more...</span>
                    </li>
                </ul>
            </template>

            <div v-else-if="hasNoMatch" class="flex flex-col items-center gap-3 px-3 py-8 text-center" data-testid="link-profile-no-match">
                <i class="bi bi-person-x text-2xl/none text-valence-grey-750" aria-hidden="true"></i>
                <h4 class="display-small-serif">No profiles match “{{ searchedName }}” at {{ organizationLabel }}.</h4>
                <p class="body-small text-valence-grey-750">
                    They may not be on Nadia yet.<br />
                    Request a profile and we'll link it here once they join.
                </p>
                <PublicProfileRequestLinkAction
                    :copied="isCopied"
                    :disabled="isCopying"
                    link-copied-text="Link Copied &mdash; share it with them"
                    class="mt-2"
                    @copy="copyRequestLink"
                    @dismiss="dismissSearch" />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useInfiniteScroll } from "@vueuse/core";
import type { OrganizationDetails } from "~vue/appTypes";
import { copyShareUrl, profileInviteMessage } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSearchInput from "~vue/components/form/BaseSearchInput.vue";
import { useLinkPublicProfile } from "~vue/composables/useLinkPublicProfile";
import { usePublicProfileSearch } from "~vue/composables/usePublicProfileSearch";
import { useTimedShow } from "~vue/composables/useTimedShow";
import PublicProfileRequestLinkAction from "~vue/Connections/PublicProfileRequestLinkAction.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import type { ConnectionDict } from "~vue/types/connection";
import type { ExistingPublicProfile } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { getFirstLastInitials, getFirstName, getLastName } from "~vue/utils/stringUtils";
import { useFilter } from "reka-ui";
import { computed, inject, nextTick, onMounted, type Ref, ref, useTemplateRef } from "vue";

const {
    connection,
    inviteUrl,
    autoFocus = false,
} = defineProps<{
    connection: ConnectionDict;
    inviteUrl: string;
    autoFocus?: boolean;
}>();

const emit = defineEmits<{ dismiss: [] }>();

const profileSearch = useTemplateRef<HTMLElement>("profileSearch");
const organizationDetails = inject<Ref<OrganizationDetails | null>>("organization_details", ref(null));
const initialSearchTerm = connection.persona.name ?? "";
const {
    query: searchQuery,
    profiles,
    error: searchError,
    isFetching: isSearching,
    hasNoMatch,
    hasMore,
    isLoadingMore,
    loadMore,
} = usePublicProfileSearch({
    initialSearchTerm,
    source: "my_people",
});

const searchedName = computed(() => searchQuery.value.trim());

// Case-insensitive ("accent" sensitivity — reka's own default collator is case-sensitive) match on
// whether the typed text starts either the first or last name.
const { startsWith } = useFilter({ sensitivity: "accent" });
const filteredProfiles = computed(() =>
    profiles.value.filter((profile) => startsWith(getFirstName(profile.name ?? ""), searchedName.value) || startsWith(getLastName(profile.name ?? ""), searchedName.value)),
);

const searchHeading = computed(() => (searchedName.value ? `Suggested matches for ${searchedName.value}` : "Suggested matches"));
const requestFirstName = computed(() => getFirstName(initialSearchTerm) || "This person");
const organizationLabel = computed(() => {
    const organization = organizationDetails.value;
    return !organization || organization.is_default || !organization.name ? "your company" : organization.name;
});

const resultsList = useTemplateRef<HTMLElement>("resultsList");
useInfiniteScroll(resultsList, loadMore, { distance: 80, canLoadMore: () => hasMore.value });

const subtitleFor = (profile: ExistingPublicProfile) => [profile.title, profile.division, profile.email].filter(Boolean).join(" · ");
const resultStatus = computed(() => {
    if (isSearching.value) {
        return "Searching collaboration profiles";
    }
    if (searchError.value) {
        return "Collaboration profile search failed";
    }
    if (isLoadingMore.value) {
        return "Loading more collaboration profiles";
    }
    if (filteredProfiles.value.length > 0) {
        return `${filteredProfiles.value.length} collaboration profile${filteredProfiles.value.length === 1 ? "" : "s"} found`;
    }
    if (hasNoMatch.value) {
        return "No collaboration profiles found";
    }
    return "";
});

const linkingProfileUserId = ref<string | null>(null);
const {
    linkProfile,
    isLinking,
    error: linkProfileError,
} = useLinkPublicProfile({
    connectionId: computed(() => connection.id),
    source: "my_people",
    onLinked: () => {
        router.reload({
            only: ["connection", "public_profile_user_id", "public_profile_basic_info"],
            onSuccess: async () => {
                await nextTick();
                document.querySelector<HTMLElement>("[data-testid='linked-profile-section']")?.focus();
            },
        });
    },
});

const showLinkProfileError = computed(() => !!linkProfileError.value && !isSessionEndedError(linkProfileError.value));

const linkSelectedProfile = (profile: ExistingPublicProfile) => {
    if (isLinking.value) {
        return;
    }
    linkingProfileUserId.value = profile.user;
    linkProfile(profile.user);
};

const { show: showCopiedRequest, isShowing: isCopied } = useTimedShow({ duration: 10_000 });
const hasCopiedRequest = ref(false);
const isCopying = ref(false);
const requestCopyFailed = ref(false);

const copyRequestLink = async () => {
    if (isCopying.value) {
        return;
    }
    isCopying.value = true;
    requestCopyFailed.value = false;
    try {
        if (!(await copyShareUrl(inviteUrl, profileInviteMessage(requestFirstName.value)))) {
            requestCopyFailed.value = true;
            return;
        }
        showCopiedRequest();
        hasCopiedRequest.value = true;
        logUserInteraction(AmplitudeEventNames.CONNECTION_INVITE_COPIED, {
            connection_id: connection.id,
            source: "my_people",
        });
    } finally {
        isCopying.value = false;
    }
};

const dismissSearch = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_LINK_SEARCH_CANCELED, {
        connection_id: connection.id,
        copied_invite: hasCopiedRequest.value,
        source: "my_people",
    });
    emit("dismiss");
};

onMounted(async () => {
    if (autoFocus) {
        await nextTick();
        profileSearch.value?.querySelector<HTMLInputElement>("input")?.focus();
    }
});
</script>
